import fetch from 'isomorphic-unfetch';
import useSWR from 'swr';
import { baseApiUrl, queryStringFromObject } from './apiHelpers';
import { firebaseApp } from './firebase';
import { MutatorCallback } from 'swr/dist/types';
import { AppApiError } from 'enums/errors';

async function fetcher<T>(url: string, locale: string, queryParams: { [index: string]: string | number } = {}) {
  const idToken = await firebaseApp?.auth()?.currentUser?.getIdToken();
  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': locale,
  };
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`;
  }
  return new Promise<T>((resolve, reject) => {
    fetch(`${baseApiUrl()}${url}${queryStringFromObject(queryParams)}`, {
      method: 'GET',
      headers,
    })
      .then((res) => {
        if (res.status === 204) {
          resolve({} as T);
        } else if (res.status > 204) {
          switch (res.status) {
            case 402:
              reject(AppApiError.PaymentNeeded);
              break;
            default:
              res.json().then(reject);
          }
        } else {
          resolve((res.json() as unknown) as T);
        }
      })
      .catch(reject);
  });
}

// hooks use on client side
export function useFetch<T>(
  path: string,
  locale: string,
  canLoad = true,
  queryParams: { [index: string]: string | number } = {},
): {
  data: T;
  error: string;
  isValidating: boolean;
  mutate: (data?: T | Promise<T> | MutatorCallback<T>, shouldRevalidate?: boolean) => Promise<T>;
} {
  if (!path) {
    throw new Error('Path is required');
  }

  const { data, error, isValidating, mutate } = useSWR<T>(
    canLoad ? path : null,
    () => fetcher<T>(path, locale, queryParams),
    {
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    },
  );

  return { data, error, isValidating, mutate };
}
